// ─── Web Push subscription helpers ───────────────────────
import api from './api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function isPushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export async function getServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  // Register (or re-use) the SW at the app root
  try {
    const reg = await navigator.serviceWorker.register('/service-worker.js');
    // Wait until it's active so PushManager is available
    if (reg.installing) {
      await new Promise((resolve) => {
        reg.installing.addEventListener('statechange', (e) => {
          if (e.target.state === 'activated') resolve();
        });
      });
    }
    return navigator.serviceWorker.ready;
  } catch (err) {
    console.error('[push] SW registration failed:', err);
    return null;
  }
}

export async function getPushSubscription() {
  const reg = await getServiceWorkerRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Your browser does not support push notifications');
  }

  // Ask for permission
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    throw new Error('Notification permission was denied');
  }

  // Fetch VAPID public key from backend
  const keyRes = await api.get('/push/public-key');
  if (!keyRes.data || !keyRes.data.publicKey) {
    throw new Error('Push is not configured on the server yet');
  }
  const appServerKey = urlBase64ToUint8Array(keyRes.data.publicKey);

  const reg = await getServiceWorkerRegistration();
  if (!reg) throw new Error('Service worker could not be registered');

  // Reuse an existing subscription if the key matches; otherwise resubscribe
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // If it exists, just re-send to backend to make sure it's recorded
    await api.post('/push/subscribe', { subscription: sub.toJSON() });
    return sub;
  }

  sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: appServerKey,
  });
  await api.post('/push/subscribe', { subscription: sub.toJSON() });
  return sub;
}

export async function unsubscribeFromPush() {
  const reg = await getServiceWorkerRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  try {
    await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
  } catch (err) { /* ignore — still try to unsubscribe locally */ }
  await sub.unsubscribe();
  return true;
}

export async function getPushStatus() {
  if (!isPushSupported()) return { supported: false, subscribed: false };
  const permission = Notification.permission;
  const sub = await getPushSubscription();
  return {
    supported: true,
    permission,
    subscribed: !!sub,
  };
}
